import React, { useEffect, useState } from "react";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { unstable_useId as useId } from "@mui/material/utils";

import ConfirmDialog from "../../shared/ConfirmDialog";
import type { DeleteMarkerScope } from "api/types/markers";

/**
 * How far a delete reaches: take the marker off this target only, or delete it
 * from every target it's applied to. A marker on a single target is removed
 * entirely, which is the same as `"all-targets"`. Re-exported from the API
 * types, so the dialog's choice and the request can't drift apart.
 */
export type { DeleteMarkerScope };

export interface DeleteMarkerDialogProps {
  /** Controls dialog visibility. */
  open: boolean;
  /** Label of the marker being deleted, quoted in the single-target message. */
  markerLabel: string;
  /**
   * How many targets the marker is applied to. Above 1 the dialog asks whether
   * to remove it from this target only or from all of them (global marker).
   * @default 1
   */
  appliedTargetCount?: number;
  /** Called when the dialog is dismissed (X icon, backdrop, or Cancel). */
  onClose: () => void;
  /** Called with the chosen scope when the user confirms the delete. */
  onConfirm: (scope: DeleteMarkerScope) => void;
  /** Shows a spinner on the confirm button and disables all actions while deleting. */
  isLoading?: boolean;
}

/**
 * Destructive confirmation for deleting a chart marker.
 *
 * A marker on a single target gets a plain confirmation. A global marker —
 * applied to more than one target — asks where to remove it from, and can't be
 * confirmed until a choice is made (Figma: "Delete this global marker?").
 *
 * @param props
 * @param props.open                 - Whether the dialog is visible
 * @param props.markerLabel          - Quoted in the single-target message
 * @param [props.appliedTargetCount=1] - Targets the marker applies to
 * @param props.onClose              - Neutral dismiss (X / backdrop / Cancel)
 * @param props.onConfirm            - Called with the chosen delete scope
 * @param [props.isLoading=false]    - Disables actions while the delete runs
 */
const DeleteMarkerDialog: React.FC<DeleteMarkerDialogProps> = ({
  open,
  markerLabel,
  appliedTargetCount = 1,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const isGlobal = appliedTargetCount > 1;
  const [scope, setScope] = useState<DeleteMarkerScope | null>(null);
  // Per-instance id tying the scope radios to their label — several dialogs can
  // render at once (e.g. Storybook docs). MUI's hook uses React.useId when
  // available and a counter on React 17.
  const scopeLabelId = useId();

  // Ask again on every open — a previous choice must never carry over into a
  // destructive action on another marker.
  useEffect(() => {
    if (open) setScope(null);
  }, [open]);

  const handleConfirm = () => {
    if (!isGlobal) {
      onConfirm("all-targets");
      return;
    }
    if (scope) onConfirm(scope);
  };

  return (
    <ConfirmDialog
      open={open}
      title={isGlobal ? "Delete this global marker?" : "Delete Marker?"}
      confirmLabel={isGlobal ? "Confirm And Delete Marker" : "Delete"}
      confirmColor="error"
      cancelLabel="Cancel"
      maxWidth="xs"
      isLoading={isLoading}
      confirmDisabled={isGlobal && scope == null}
      onClose={onClose}
      onCancel={onClose}
      onConfirm={handleConfirm}
    >
      {isGlobal ? (
        <Stack spacing={4} sx={{ py: 2 }}>
          <Typography variant="body.base" color="text.secondary">
            This marker is applied to more than one clinical target. Should this be removed from all
            targets?
          </Typography>

          <FormControl disabled={isLoading}>
            <FormLabel
              id={scopeLabelId}
              sx={{ typography: "caption", fontWeight: 500, color: "text.secondary", mb: 1 }}
            >
              Remove this marker from
            </FormLabel>
            <RadioGroup
              aria-labelledby={scopeLabelId}
              value={scope ?? ""}
              onChange={(event) => setScope(event.target.value as DeleteMarkerScope)}
            >
              <FormControlLabel
                value="this-target"
                control={<Radio size="small" />}
                label="This target only"
              />
              <FormControlLabel
                value="all-targets"
                control={<Radio size="small" />}
                label="All applied targets"
              />
            </RadioGroup>
          </FormControl>
        </Stack>
      ) : (
        <Typography variant="body.base" color="text.secondary" sx={{ py: 2 }}>
          This will permanently remove the marker &lsquo;{markerLabel}&rsquo; from this chart. This
          action cannot be undone.
        </Typography>
      )}
    </ConfirmDialog>
  );
};

export default DeleteMarkerDialog;
